import JsSIP from 'jssip'
import type { RTCSession } from 'jssip/lib/RTCSession'
import { ReactNode, createContext, useEffect, useRef, useState } from 'react'

import { CallStatus } from '@/types'

const socket = new JsSIP.WebSocketInterface(process.env.REACT_APP_JS_SIP_SOCKET || '')
const ua = new JsSIP.UA({
  sockets: [socket],
  uri: process.env.REACT_APP_JS_SIP_URI || '',
  password: process.env.REACT_APP_JS_SIP_PASSWORD || '',
})
ua.start()

export const CallContext = createContext<{
  status: CallStatus
  isIncoming: boolean
  target: string
  modalIsVisible: boolean
  openModal: (phone: string) => void
  closeModal: () => void
  makeCall: () => void
  endCall: () => void
  answerCall: () => void
  holdCall: () => void
  unholdCall: () => void
    }>({
      status: '',
      isIncoming: false,
      target: '',
      modalIsVisible: false,
      openModal: () => {},
      closeModal: () => {},
      makeCall: () => {},
      endCall: () => {},
      answerCall: () => {},
      holdCall: () => {},
      unholdCall: () => {},
    })

export function CallProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<CallStatus>('')
  const [isIncoming, setIsIncoming] = useState(false)
  const [target, setTarget] = useState('')
  const [session, setSession] = useState<RTCSession | null>(null)
  const [modalIsVisible, setModalIsVisible] = useState(false)

  const localStream = useRef<MediaStream>()

  const localAudio = useRef<HTMLAudioElement | null>(null)
  const remoteAudio = useRef<HTMLAudioElement | null>(null)

  const openModal = (phone: string) => {
    if (modalIsVisible) return

    setTarget(phone)
    setModalIsVisible(true)
  }

  const makeCall = () => {
    if (session) {
      session.terminate()
      setSession(null)
    }

    setStatus('outgoing')
    setIsIncoming(false)

    if (!localStream.current) {
      getUserMedia()
    }

    const newSession = ua.call(target, {
      mediaStream: localStream.current,
      pcConfig: {
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      },
    })

    setSession(newSession)

    handleRemoteAudio(newSession)
    bindSessionEvents(newSession)
  }

  const endCall = () => {
    if (!session) return

    session.terminate()
  }

  const answerCall = () => {
    if (!session || session.direction !== 'incoming') return

    if (!localStream.current) {
      getUserMedia()
    }

    session.answer({
      mediaStream: localStream.current,
      pcConfig: {
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      },
    })

    handleRemoteAudio(session)
  }

  const holdCall = () => {
    if (!session) return

    session.hold()
  }

  const unholdCall = () => {
    if (!session) return

    session.unhold()
  }

  const handleRemoteAudio = (session: RTCSession | null) => {
    if (!session) return

    const pc = session.connection

    pc.ontrack = (event) => {
      if (remoteAudio.current) {
        remoteAudio.current.srcObject = event.streams[0]
      }
    }
  }

  const bindSessionEvents = (session: RTCSession | null) => {
    if (!session) return

    session.on('confirmed', () => {
      setStatus('connected')
    })

    session.on('ended', () => {
      setStatus('ended')
      setSession(null)
    })

    session.on('failed', (e) => {
      setStatus('failed')
      setSession(null)
    })

    session.on('hold', () => {
      setStatus('paused')
    })

    session.on('unhold', () => {
      setStatus('unpaused')
    })
  }

  const getUserMedia = () => {
    navigator.mediaDevices
      .getUserMedia({ audio: true, video: false })
      .then((stream) => {
        localStream.current = stream
        if (localAudio.current) {
          localAudio.current.srcObject = stream
        }
      })
      .catch(console.error)
  }

  const closeModal = () => {
    setModalIsVisible(false)
    setStatus('')
    setSession(null)
    setIsIncoming(false)
  }

  useEffect(() => {
    getUserMedia()

    ua.on('newRTCSession', (e: any) => {
      const incomingSession = e.session

      if (session) {
        incomingSession.terminate()
        return
      }

      setSession(incomingSession)

      if (incomingSession.direction === 'incoming') {
        setStatus(`incoming`)
        setIsIncoming(true)
        setTarget(incomingSession.remote_identity.uri.toString())
        setModalIsVisible(true)
      }

      bindSessionEvents(incomingSession)
    })
  }, [])

  return (
    <CallContext.Provider
      value={{
        status,
        isIncoming,
        target,
        modalIsVisible,
        openModal,
        closeModal,
        makeCall,
        endCall,
        answerCall,
        holdCall,
        unholdCall,
      }}
    >
      {children}
      <audio
        ref={localAudio}
        autoPlay
        muted
        style={{ display: 'none' }}
      />
      <audio
        ref={remoteAudio}
        autoPlay
        style={{ display: 'none' }}
      />
    </CallContext.Provider>
  )
}
